import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PlusCircle, Save, X } from "lucide-react";
import { useMutation, gql } from "@apollo/client";

const EDIT_CASE = gql`
  mutation EditCase(
    $odrnumber: Int
    $participant: Int!
    $bookingNumber: String!
    $caseNumber: String!
    $case: EditCaseInput!
  ) {
    editCase(
      odrnumber: $odrnumber
      participant: $participant
      bookingNumber: $bookingNumber
      caseNumber: $caseNumber
      case: $case
    ) {
      message
    }
  }
`;

export const EditCaseModal = ({
  isOpen,
  onClose,
  caseData,
  odrNumber,
  participant,
  bookingNumber,
  refetch,
}: {
  isOpen: boolean;
  onClose: () => void;
  caseData: any;
  odrNumber: number;
  participant: number;
  bookingNumber: string;
  refetch: () => void;
  }) => {
	  const [formData, setFormData] = useState({
	    caseNumber: "",
	    status: "open",
	    caseOutcome: "",
	    hideFromQuarterly: false,
	    charges: [{ charge: "", charge_entrydate: new Date().toISOString() }],
	  });

  const [editCase, { loading: saving }] = useMutation(EDIT_CASE);

  useEffect(() => {
    if (caseData) {
      setFormData({
	        caseNumber: caseData.caseNumber || "",
	        status: caseData.caseOpen ? "open" : "closed",
	        caseOutcome: caseData.caseOutcome || "",
	        hideFromQuarterly: caseData.hideFromQuarterly === true || String(caseData.hideFromQuarterly) === "1",
	        charges: caseData.charges?.length
          ? caseData.charges
          : [{ charge: "", charge_entrydate: new Date().toISOString() }],
      });
    }
  }, [caseData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleStatusChange = (value: string) => {
    setFormData((prev) => ({ ...prev, status: value }));
  };

  const handleChargeChange = (index: number, value: string) => {
    setFormData((prev) => {
      const newCharges = [...prev.charges];
      newCharges[index] = {
        ...newCharges[index],
        charge: value,
        charge_entrydate: new Date().toISOString(),
      };
      return { ...prev, charges: newCharges };
    });
  };

  const addCharge = () => {
    setFormData((prev) => ({
      ...prev,
      charges: [
        ...prev.charges,
        { charge: "", charge_entrydate: new Date().toISOString() },
      ],
    }));
  };

  const removeCharge = (index: number) => {
    if (formData.charges.length > 1) {
      setFormData((prev) => ({
        ...prev,
        charges: prev.charges.filter((_, i) => i !== index),
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const cleanedCharges = formData.charges
        .filter(charge => charge.charge.trim() !== "")
        .map(({ charge, charge_entrydate }) => ({
          charge,
          charge_entrydate
        }));

      await editCase({
        variables: {
          odrnumber: odrNumber,
          participant,
          bookingNumber,
          caseNumber: caseData.caseNumber,
          case: {
	            caseNumber: formData.caseNumber,
	            caseOpen: formData.status === "open",
	            caseOutcome: formData.caseOutcome.trim() || null,
	            hideFromQuarterly: formData.hideFromQuarterly,
	            charges: cleanedCharges,
          },
        },
      });
      refetch();
      onClose();
    } catch (error) {
      console.error("Error editing case:", error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden flex flex-col h-[80vh]">
        <DialogHeader className="p-4 border-b flex flex-row items-center justify-between bg-white shrink-0">
          <DialogTitle className="text-xl font-bold text-gray-800">
            Edit Case: {caseData?.caseNumber}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6 bg-gray-50/30">
          <form id="edit-case-form" onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm space-y-6">
	              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase text-gray-500">Case Number</Label>
                  <Input
                    id="caseNumber"
                    name="caseNumber"
                    value={formData.caseNumber}
                    onChange={handleInputChange}
                    className="h-9"
                    required
                  />
                </div>

	                <div className="space-y-2">
	                  <Label htmlFor="status" className="text-xs font-bold uppercase text-gray-500">Case Status</Label>
                  <Select value={formData.status} onValueChange={handleStatusChange}>
                    <SelectTrigger className="w-full h-9">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
	                  </Select>
	                </div>

	                <div className="space-y-2">
	                  <Label htmlFor="caseOutcome" className="text-xs font-bold uppercase text-gray-500">Case Outcome</Label>
	                  <Input
	                    id="caseOutcome"
	                    name="caseOutcome"
	                    value={formData.caseOutcome}
	                    onChange={handleInputChange}
	                    className="h-9"
	                  />
	                </div>

	                <div className="flex items-center gap-2 self-end pb-2">
	                  <Checkbox
	                    id="hideFromQuarterly"
	                    checked={formData.hideFromQuarterly}
	                    onCheckedChange={(checked) =>
	                      setFormData((prev) => ({ ...prev, hideFromQuarterly: checked === true }))
	                    }
	                  />
	                  <Label htmlFor="hideFromQuarterly" className="text-xs font-bold uppercase text-gray-500">
	                    Hide from quarterly
	                  </Label>
	                </div>
	              </div>

              <div className="space-y-4 pt-4 border-t">
                <div className="flex justify-between items-center">
                  <Label className="text-xs font-bold uppercase text-gray-500">Charges</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addCharge}
                    className="flex items-center gap-1.5 h-8"
                  >
                    <PlusCircle className="h-3.5 w-3.5" />
                    Add Charge
                  </Button>
                </div>

                <div className="space-y-3">
                  {formData.charges.map((charge, index) => (
                    <div key={index} className="flex gap-2 items-start group">
                      <Input
                        value={charge.charge}
                        onChange={(e) => handleChargeChange(index, e.target.value)}
                        placeholder="Enter charge description"
                        className="flex-1 h-9"
                      />
                      {formData.charges.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeCharge(index)}
                          className="h-9 w-9 p-0 text-gray-400 hover:text-rose-600 transition-colors"
                          title="Remove Charge"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </form>
        </div>

        <div className="flex items-center justify-between p-4 border-t bg-gray-50 shrink-0">
          <Button 
            type="submit" 
            form="edit-case-form" 
            size="sm"
            className="gap-1.5 px-6 shadow-sm"
            disabled={saving}
          >
            <Save className="h-4 w-4" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
